import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, GraduationCap, UtensilsCrossed, CheckCircle, AlertCircle, Wrench, Star, Quote, MapPin } from "lucide-react";
import insdLogo from "@/assets/clients/insd-logo.png";
import maacLogo from "@/assets/clients/maac-logo.png";
import frameboxxLogo from "@/assets/clients/frameboxx-logo.png";

interface CaseStudy {
  id: string;
  client: string;
  fullName?: string;
  industry?: string;
  locations?: string;
  logo?: string;
  problems: string[];
  solutions: string[];
  results: string[];
  review?: {
    text: string;
    author: string;
    role: string;
    rating: number;
  };
}

const educationCaseStudies: CaseStudy[] = [
  {
    id: "insd",
    client: "INSD",
    fullName: "International School of Design",
    locations: "3 Locations",
    logo: insdLogo,
    problems: [
      "Need to attract the right student profile across 3 locations",
      "High drop-off after inquiry",
      "Inconsistent lead quality across centers",
    ],
    solutions: [
      "Refined targeting strategy per location",
      "Integrated funnel across all 3 centers with unified CRM tracking",
      "Introduced nurturing workflows with WhatsApp + Email",
      "Location-specific landing pages with structured intake forms",
    ],
    results: [
      "62% increase in qualified applications",
      "3x improvement in inquiry-to-enrollment rate",
      "40% reduction in cost per qualified lead",
      "Unified reporting dashboard across all locations",
    ],
    review: {
      text: "TheGrowthBy transformed our student acquisition across all 3 centers. Their integrated funnel approach gave us consistency and visibility we never had before.",
      author: "Varsha Chandwani",
      role: "Founder, INSD",
      rating: 5,
    },
  },
  {
    id: "maac",
    client: "MAAC",
    fullName: "Maya Academy of Advanced Creativity",
    locations: "2 Locations",
    logo: maacLogo,
    problems: [
      "High competition in student acquisition",
      "Need for consistent lead flow across 2 centers",
      "Inefficient tracking of leads between locations",
    ],
    solutions: [
      "Built structured lead generation campaigns per location",
      "Integrated funnel across both centers with shared CRM",
      "Optimized campaigns based on center-level performance data",
      "Automated follow-up sequences for each inquiry stage",
    ],
    results: [
      "85% increase in consistent lead volume",
      "2.5x improvement in campaign efficiency",
      "45% better conversion from inquiry to enrollment",
      "Real-time cross-location performance visibility",
    ],
    review: {
      text: "Working with TheGrowthBy gave us the structure and consistency we needed across both our centers. Lead quality and conversion improved significantly.",
      author: "Namita Baheti",
      role: "Director Marketing & Sales, MAAC",
      rating: 5,
    },
  },
  {
    id: "nsdt",
    client: "NSDT",
    fullName: "National School of Design and Technology",
    locations: "Corporate + 2 Franchise",
    problems: [
      "Low-quality leads across corporate and franchise locations",
      "Poor conversion from inquiry to enrollment",
      "No unified system across franchise and corporate centers",
    ],
    solutions: [
      "Improved targeting and messaging per location type",
      "Integrated funnel across corporate and 2 franchise centers",
      "Introduced structured lead handling with qualification scoring",
      "Unified CRM with location-level reporting",
    ],
    results: [
      "70% improvement in lead quality",
      "3x better conversion rates across all locations",
      "55% more qualified inquiries month-over-month",
      "Standardized process across franchise and corporate",
    ],
    review: {
      text: "TheGrowthBy helped us unify our lead generation across corporate and franchise models. The integrated approach made a real difference in quality and consistency.",
      author: "Aakash Ahuja",
      role: "Director Global Marketing, NSDT",
      rating: 5,
    },
  },
  {
    id: "frameboxx",
    client: "Frameboxx",
    fullName: "Frameboxx Institute of Emerging Media",
    logo: frameboxxLogo,
    problems: [
      "Difficulty standing out in a crowded creative education market",
      "Low brand recall among prospective students",
      "Inconsistent digital presence",
    ],
    solutions: [
      "Revamped digital ad strategy with creative-first approach",
      "Built high-converting landing pages with student testimonials",
      "Implemented structured nurture sequences",
      "Content strategy showcasing student work and outcomes",
    ],
    results: [
      "90% increase in qualified student inquiries",
      "2x improvement in ad ROAS",
      "60% reduction in inquiry drop-off rate",
      "Stronger brand presence in target markets",
    ],
    review: {
      text: "The team at TheGrowthBy understood the creative education space and delivered campaigns that truly resonated with our target audience.",
      author: "Snehal Chauhan",
      role: "Frameboxx",
      rating: 5,
    },
  },
];

const serviceCaseStudies: CaseStudy[] = [
  {
    id: "dental",
    client: "M.D. Eye Care & Laser Centre",
    fullName: "M.D. Eye Care & Laser Centre",
    industry: "Healthcare",
    locations: "2 Locations Served",
    problems: [
      "Inconsistent lead flow across 2 locations",
      "No structured follow-up system",
      "Potential leads not converting into bookings",
      "No visibility into which location was performing better",
    ],
    solutions: [
      "Implemented targeted ad campaigns per location",
      "Built an integrated lead capture and routing system across both centers",
      "Introduced instant follow-up via messaging",
      "Unified CRM with location-level performance tracking",
    ],
    results: [
      "3x increase in consistent inbound leads",
      "75% improvement in response time to new inquiries",
      "2x increase in appointment booking rates",
      "Clear ROI visibility per location",
    ],
    review: {
      text: "TheGrowthBy helped us streamline patient acquisition across both our locations. The integrated system gave us control and visibility we didn't have before.",
      author: "Dr Alkesh Chaudhary",
      role: "Founder, M.D. Eye Care & Laser Centre",
      rating: 5,
    },
  },
  {
    id: "smile-dental",
    client: "Smile The Dental Studio",
    fullName: "Smile The Dental Studio",
    industry: "Dental Clinic",
    problems: [
      "Low online visibility in a competitive local market",
      "Leads not converting into booked appointments",
      "No follow-up system for missed inquiries",
    ],
    solutions: [
      "Launched hyper-local Google and Meta ad campaigns",
      "Built optimized booking funnel with instant confirmation",
      "Introduced automated SMS/WhatsApp follow-ups",
      "Implemented review generation workflow",
    ],
    results: [
      "4x increase in monthly appointment bookings",
      "65% reduction in cost per patient acquisition",
      "80% of inquiries now booked within 24 hours",
      "50+ new Google reviews generated in 3 months",
    ],
    review: {
      text: "From the very first month, we saw a noticeable difference in both the quality and quantity of patient inquiries. Highly recommend TheGrowthBy.",
      author: "Dr Priya Oberoi",
      role: "Smile The Dental Studio",
      rating: 5,
    },
  },
];

const restaurantCaseStudies: CaseStudy[] = [
  {
    id: "flaming-tikkas",
    client: "Flaming Tikkas",
    fullName: "Flaming Tikkas – Multi-Location Restaurant",
    industry: "Restaurant",
    locations: "Multiple Locations",
    problems: [
      "Low repeat customer rate across locations",
      "Inconsistent online order volume",
      "High dependency on aggregator platforms",
      "No structured marketing or follow-up system",
    ],
    solutions: [
      "Launched targeted social media and Google ad campaigns per location",
      "Built automated follow-up sequences for online orders",
      "Implemented QR-based customer capture for repeat engagement",
      "Integrated funnel across all locations with unified reporting",
      "WhatsApp automation for re-engagement and promotions",
    ],
    results: [
      "40% increase in repeat orders",
      "2.5x growth in direct online orders (off-aggregator)",
      "Consistent daily order volume across all locations",
      "35% of customers now ordering directly via owned channels",
    ],
    review: {
      text: "TheGrowthBy helped us take control of our customer relationships across all locations. The repeat order system alone has been a game changer for our business.",
      author: "Rohit Gupta",
      role: "Founder, Flaming Tikkas",
      rating: 5,
    },
  },
];

const ReviewCard = ({ review }: { review: NonNullable<CaseStudy["review"]> }) => (
  <div className="mt-6 bg-muted/50 rounded-lg p-5 border border-border/50">
    <div className="flex items-start gap-3">
      <Quote className="h-8 w-8 text-primary/30 shrink-0 mt-1" />
      <div>
        <p className="text-sm text-muted-foreground italic mb-3">"{review.text}"</p>
        <div className="flex items-center gap-2 mb-1">
          {[...Array(review.rating)].map((_, i) => (
            <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
          ))}
        </div>
        <p className="text-sm font-semibold text-foreground">{review.author}</p>
        <p className="text-xs text-muted-foreground">{review.role}</p>
      </div>
    </div>
  </div>
);

const CaseStudyCard = ({ study, index }: { study: CaseStudy; index: number }) => (
  <motion.div
    id={study.id}
    className="bg-card border border-border rounded-xl p-6 md:p-8 scroll-mt-24"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
  >
    {/* Header with logo */}
    <div className="flex items-start gap-5 mb-6">
      {study.logo && (
        <div className="w-20 h-20 rounded-lg bg-white border border-border flex items-center justify-center p-2 shrink-0">
          <img src={study.logo} alt={study.client} className="w-full h-full object-contain" />
        </div>
      )}
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-foreground mb-1">{study.client}</h3>
        {study.fullName && study.fullName !== study.client && (
          <p className="text-sm text-muted-foreground">{study.fullName}</p>
        )}
        <div className="flex items-center gap-3 mt-1">
          {study.industry && (
            <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-full">{study.industry}</span>
          )}
          {study.locations && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {study.locations}
            </span>
          )}
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-3 gap-6">
      {/* Problems */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <h4 className="font-semibold text-foreground">Problem</h4>
        </div>
        <ul className="space-y-2">
          {study.problems.map((p, i) => (
            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-destructive/60 mt-1.5 shrink-0" />
              {p}
            </li>
          ))}
        </ul>
      </div>

      {/* Solutions */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Wrench className="h-5 w-5 text-primary" />
          <h4 className="font-semibold text-foreground">Solution</h4>
        </div>
        <ul className="space-y-2">
          {study.solutions.map((s, i) => (
            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-1.5 shrink-0" />
              {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Results */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <h4 className="font-semibold text-foreground">Results</h4>
        </div>
        <ul className="space-y-2">
          {study.results.map((r, i) => (
            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500/60 mt-1.5 shrink-0" />
              <span className="font-medium">{r}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    {/* Review */}
    {study.review && <ReviewCard review={study.review} />}
  </motion.div>
);

interface CaseStudyTabsProps {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

export const CaseStudyTabs = ({ activeTab = "education", onTabChange }: CaseStudyTabsProps) => {
  return (
    <section className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
          <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3 mb-12">
            <TabsTrigger value="education" className="gap-2">
              <GraduationCap className="h-4 w-4" />
              Education
            </TabsTrigger>
            <TabsTrigger value="services" className="gap-2">
              <Building2 className="h-4 w-4" />
              Service Businesses
            </TabsTrigger>
            <TabsTrigger value="restaurants" className="gap-2">
              <UtensilsCrossed className="h-4 w-4" />
              Restaurants
            </TabsTrigger>
          </TabsList>

          <TabsContent value="education" className="space-y-8">
            {educationCaseStudies.map((study, i) => (
              <CaseStudyCard key={study.id} study={study} index={i} />
            ))}
          </TabsContent>

          <TabsContent value="services" className="space-y-8">
            {serviceCaseStudies.map((study, i) => (
              <CaseStudyCard key={study.id} study={study} index={i} />
            ))}
          </TabsContent>

          <TabsContent value="restaurants" className="space-y-8">
            {restaurantCaseStudies.map((study, i) => (
              <CaseStudyCard key={study.id} study={study} index={i} />
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};
